import type { SegmentConfig } from "@sg-protect/odata";
import type { ODataLinkType, ODataReviewType, ODataUserType } from "../../api/types.ts";

export type StepGroup = "lifecycle" | "sharingLinks" | "membership" | "files";

export const STEP_GROUPS: StepGroup[] = ["lifecycle", "sharingLinks", "membership", "files"];

export function groupOfReviewType(type: ODataReviewType): StepGroup | undefined {
    switch (type) {
        case "lifecycle":
            return "lifecycle";
        case "externalSharing":
        case "internalSharing":
            return "sharingLinks";
        case "guests":
        case "members":
            return "membership";
        case "files":
            return "files";
        default:
            return undefined;
    }
}

export function reviewStepsOf(reviewTypes: ODataReviewType[], isFilesEnabled: boolean): StepGroup[] {
    const groups = new Set<StepGroup>();

    for (const type of reviewTypes) {
        const group = groupOfReviewType(type);
        if (group != null && (group !== "files" || isFilesEnabled)) {
            groups.add(group);
        }
    }

    return STEP_GROUPS.filter(group => groups.has(group));
}

export type SharingLinkScope = "external" | "internal" | "all";

export function sharingLinkScopeOf(reviewTypes: ODataReviewType[]): SharingLinkScope | undefined {
    const hasExternal = reviewTypes.includes("externalSharing");
    const hasInternal = reviewTypes.includes("internalSharing");

    if (hasExternal && hasInternal) {
        return "all";
    }

    if (hasExternal) {
        return "external";
    }

    return hasInternal ? "internal" : undefined;
}

const EXTERNAL_LINK_TYPES: ODataLinkType[] = ["anyone", "externalPeople", "bothInternalAndExternalPeople"];
const INTERNAL_LINK_TYPES: ODataLinkType[] = ["peopleInYourOrg", "internalPeople", "bothInternalAndExternalPeople"];

const toLinkTypeFilter = (types: ODataLinkType[]) => types.map(type => `linkType eq '${type}'`).join(" or ");

export const SHARING_LINK_BASE_FILTERS: Record<SharingLinkScope, string | undefined> = {
    external: toLinkTypeFilter(EXTERNAL_LINK_TYPES),
    internal: toLinkTypeFilter(INTERNAL_LINK_TYPES),
    all: undefined
};

// Labels stay English: useSegmentFilter sends them to analytics. The step renders its catalog keys instead.
export const SHARING_LINK_SEGMENTS: SegmentConfig<SharingLinkScope>[] = [
    {
        id: "all",
        label: "All",
        expression: undefined
    },
    {
        id: "external",
        label: "External",
        expression: SHARING_LINK_BASE_FILTERS.external
    },
    {
        id: "internal",
        label: "Internal",
        expression: SHARING_LINK_BASE_FILTERS.internal
    }
];

export type MembershipScope = "guests" | "internal" | "all";

export function membershipScopeOf(reviewTypes: ODataReviewType[]): MembershipScope | undefined {
    const hasGuests = reviewTypes.includes("guests");
    const hasMembers = reviewTypes.includes("members");

    if (hasGuests && hasMembers) {
        return "all";
    }

    if (hasGuests) {
        return "guests";
    }

    if (hasMembers) {
        return "internal";
    }

    return undefined;
}

const toUserTypeFilter = (type: ODataUserType) => `type eq '${type}'`;

export const MEMBERSHIP_BASE_FILTERS: Record<MembershipScope, string | undefined> = {
    guests: toUserTypeFilter("guest"),
    internal: toUserTypeFilter("member"),
    all: undefined
};

// Labels stay English: useSegmentFilter sends them to analytics. The step renders its catalog keys instead.
export const MEMBERSHIP_SEGMENTS: SegmentConfig<MembershipScope>[] = [
    {
        id: "all",
        label: "All",
        expression: undefined
    },
    {
        id: "internal",
        label: "Internal members",
        expression: MEMBERSHIP_BASE_FILTERS.internal
    },
    {
        id: "guests",
        label: "External guests",
        expression: MEMBERSHIP_BASE_FILTERS.guests
    }
];
